import Vue from 'vue'
import Vuex from 'vuex'
import axios from './axios-auth'
import router from '../router/index.js'
import createPersistedState from "vuex-persistedstate";
import { getProductById } from '@/utils/productData';
import { shopCardImage } from '@/utils/productImage';

Vue.use(Vuex)

function toNumericPrice(value) {
  if (value === null || value === undefined || value === '') return NaN;
  const n = parseFloat(String(value).replace('$', ''));
  return isNaN(n) ? NaN : n;
}

function lookupCachedPricing(id) {
  if (!id) return null;
  try {
    const matches = getProductById(id);
    const product = Array.isArray(matches) ? matches[0] : matches;
    return product && product.pricing ? product.pricing : null;
  } catch (_) {
    return null;
  }
}

function resolveItemPrice(item) {
  const direct = toNumericPrice(item && item.price);
  if (!isNaN(direct)) return direct;
  const pricing = lookupCachedPricing(item && item.id);
  if (pricing) {
    const candidate = toNumericPrice(pricing.discounted_price);
    if (!isNaN(candidate)) return candidate;
    const retail = toNumericPrice(pricing.retail_price);
    if (!isNaN(retail)) return retail;
    const plain = toNumericPrice(pricing.price);
    if (!isNaN(plain)) return plain;
  }
  return 0;
}

function resolveItemRetailPrice(item) {
  const direct = toNumericPrice(item && item.retail_price);
  if (!isNaN(direct)) return direct;
  const pricing = lookupCachedPricing(item && item.id);
  if (pricing) {
    const retail = toNumericPrice(pricing.retail_price);
    if (!isNaN(retail)) return retail;
    const plain = toNumericPrice(pricing.price);
    if (!isNaN(plain)) return plain;
  }
  return resolveItemPrice(item);
}

export default new Vuex.Store({
  state: {
    // Existing state
    accessToken: null,
    userData: [],
    progressItems: [],
    lastCheckedProgress: null,
    loggingIn: false,
    transactionItems: [],
    activeHomeTab: 'myNewSelf',
    latestPrescription:[],
    appointments: null,
    lab: null,
    priscription: null,
    shipping: null,
    tracking: null,
    ipAddress: null,
    medForm: null,
    prescriptionPreauths: null,
    lastCheckedSubscription: null,
    subscriptionItems:[],
    lastCheckedTransactions: null,
    trackingData: [], 
    // Cart state (legacy - used by shoppingCart.vue, productTemplate.vue, etc.)
    cartItems: [],
    isCartOpen: false,
    // Store cart (dashboard store only - completely separate from legacy cart)
    storeCartItems: [],
    weightLossOffer: null,
    encounter:null,
    // New product-related state
    currentCategory: 'WEIGHT LOSS',
    loading: false,
    docChat: false,
    error: null,
    offerSema: 'sema_plan_278928',
    offerTir: 'tirz_plan_1',
    chatProfile:null,
    case: {id: "" , provider: ""},
    doctorProfile:[],
    addressData:[],
    // Dashboard theme
    theme: 'light',
    // Referral
    referralLink: null,
    // Store visibility: tracks med form submission for 12h countdown
    // { orderId: String, timestamp: Number } | null
    medFormSubmission: null
  },
  
  mutations: {
    SET_SEMA_OFFER(state, offer) {
      const offerSemaMap = {
        A: 'sema_plan_134562',
        C: 'sema_plan_367367',
      };
    
      // Assign based on offer, defaulting to 'sema_plan_278928'
      state.offerSema = offerSemaMap[offer] || 'sema_plan_278928';
    },

    SET_TIR_OFFER(state, offer) {
      const offerTirMap = {
        C: 'tirz_plan_3',
        B: 'tirz_plan_2',
      };
      // Assign based on offer, defaulting to 'tirz_plan_1'
      state.offerTir = offerTirMap[offer] || 'tirz_plan_1';
    },

    SET_Transactions_ITEMS(state, payload) {
      
      if (payload && payload.items) {
        state.transactionItems = payload.items;
        state.lastCheckedTransactions = payload.timestamp;
      }
    },
    SET_SUBSCRIPTION_ITEMS(state, payload) {
      if (payload && payload.items) {
        state.subscriptionItems = payload.items;
        state.lastCheckedSubscription = payload.timestamp;
      }
    },
    SET_PROGRESS_ITEMS(state, payload) {
      
      if (payload && payload.items) {
        state.progressItems = payload.items;
        state.lastCheckedProgress = payload.timestamp;
      }
    },
    SET_PRESCRIPTION_PREAUTHS(state, preauths) {
      state.priscription = preauths;
    },
    SET_DOC_CHAT(state) {
      state.docChat = !state.docChat;
    },
    REMOVE_PRESCRIPTION(state, orderId) {
      state.priscription = state.priscription.filter(
        preauth => preauth.orderId !== orderId
      );
    },
    // Existing mutations remain the same
    loginStart: state => state.loggingIn = true,
    updateAccessToken: (state, accessToken) => {
      state.accessToken = accessToken;
    },
    updateUserData: (state, userData) => {
      state.userData = { ...state.userData, ...userData }
    },
    logout: (state) => {
      state.accessToken = null;
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
    },
    
    SaveShippingDetails: (state, shipping) => {
      state.shipping = shipping
    },
    setIpAddress(state, ipAddress) {
      state.ipAddress = ipAddress;
    },
    SET_ACTIVE_HOME_TAB(state, tab) {
        state.activeHomeTab = tab;
    },
    // Cart mutations
    ADD_TO_CART(state, item) {
      const existingItem = state.cartItems.find(i => i.id === item.id);

      const pricing = lookupCachedPricing(item && item.id) || {};
      const pickFirstNumeric = (...candidates) => {
        for (const c of candidates) {
          const n = toNumericPrice(c);
          if (!isNaN(n)) return n;
        }
        return undefined;
      };
      let resolvedPrice = pickFirstNumeric(item.price, pricing.discounted_price, pricing.retail_price, pricing.price);
      let resolvedRetail = pickFirstNumeric(item.retail_price, pricing.retail_price, pricing.price, resolvedPrice);

      if (resolvedPrice === undefined) {
        console.warn('ADD_TO_CART: no price resolvable for item, defaulting to 0', item && item.id);
        resolvedPrice = 0;
      }
      if (resolvedRetail === undefined) {
        resolvedRetail = resolvedPrice;
      }

      const newItem = {
          id: item.id,
          name: item.name,
          price: resolvedPrice,
          retail_price: resolvedRetail,
          quantity: item.quantity,
          main_type: item.main_type,
          subheader: item.subheader,
          image: item.image,
          category: item.category,
          sku: item.sku,
          stripe_id: item.stripe_id,
          is_addon: item.is_addon
      };
      if (existingItem && !item.is_addon) {
        existingItem.quantity += item.quantity || 1;
      } else {
          state.cartItems.push(newItem);
      }
      /*if (item.main_type === "prescription") {
          if (!existingItem) {
              state.cartItems.push(newItem);
          }
      } else {
          if (existingItem) {
              existingItem.quantity += 1;
          } else {
              state.cartItems.push(newItem);
          }
      }*/
  },
    
    REMOVE_FROM_CART(state, itemId) {
      state.cartItems = state.cartItems.filter(item => item.id !== itemId);
    },
    
    UPDATE_CART_QUANTITY(state, { itemId, quantity }) {
      const item = state.cartItems.find(i => i.id === itemId);
      if (item) {
        if (item.main_type === "Prescription") {
          item.quantity = 1;
        } else {
          if (quantity > 0) {
            item.quantity = quantity;
          } else {
            state.cartItems = state.cartItems.filter(i => i.id !== itemId);
          }
        }
      }
    },
    
    UPDATE_CART_ITEM(state, { itemId, updates }) {
      const idx = state.cartItems.findIndex(i => i.id === itemId);
      if (idx !== -1) {
        const updated = Object.assign({}, state.cartItems[idx], updates);
        Vue.set(state.cartItems, idx, updated);
      }
    },

    CLEAR_CART(state) {
      state.cartItems = [];
    },

    // Store cart mutations (dashboard store - separate from legacy)
    STORE_ADD_TO_CART(state, item) {
      const existingItem = state.storeCartItems.find(i => i.id === item.id && i.terms === item.terms);
      const newItem = {
        id: item.id,
        name: item.name,
        price: item.price,
        retail_price: item.retail_price,
        quantity: item.quantity || 1,
        main_type: item.main_type,
        subheader: item.subheader,
        image: item.image || item.thumbnail,
        thumbnail: item.thumbnail || item.image,
        category: item.category,
        sku: item.sku,
        stripe_id: item.stripe_id,
        is_addon: item.is_addon,
        plan_id: item.plan_id || null,
        subscriptionPrice: item.subscriptionPrice,
        isSubscription: item.isSubscription || false,
        terms: item.terms != null ? item.terms : null,
        totalTermPrice: item.totalTermPrice || null,
        variantLabel: item.variantLabel || null
      };
      if (existingItem && !item.is_addon) {
        existingItem.quantity += item.quantity || 1;
      } else {
        state.storeCartItems.push(newItem);
      }
    },

    STORE_REMOVE_FROM_CART(state, itemId) {
      state.storeCartItems = state.storeCartItems.filter(item => item.id !== itemId);
    },

    STORE_UPDATE_CART_QUANTITY(state, { itemId, quantity }) {
      const item = state.storeCartItems.find(i => i.id === itemId);
      if (item) {
        if (item.main_type === 1 || item.main_type === 'prescription') {
          item.quantity = 1;
        } else if (quantity > 0) {
          item.quantity = quantity;
        } else {
          state.storeCartItems = state.storeCartItems.filter(i => i.id !== itemId);
        }
      }
    },

    STORE_UPDATE_CART_ITEM(state, { itemId, updates }) {
      const idx = state.storeCartItems.findIndex(i => i.id === itemId);
      if (idx !== -1) {
        const updated = Object.assign({}, state.storeCartItems[idx], updates);
        Vue.set(state.storeCartItems, idx, updated);
      }
    },

    STORE_CLEAR_CART(state) {
      state.storeCartItems = [];
    },

    SET_CART_VISIBILITY(state, isVisible) {
      state.isCartOpen = isVisible;
    },

    // New product-related mutations
    SET_CURRENT_CATEGORY(state, category) {
      state.currentCategory = category;
    },
    
    SET_LOADING(state, status) {
      state.loading = status;
    },
    
    SET_ERROR(state, error) {
      state.error = error;
    },
    SET_CHAT_PROFILE(state, profile) {
      state.chatProfile = profile;
    },
    SET_NEW_PHONE(state, phone) {
      state.userData.phone = phone;
    },
    SET_NEW_EMAIL(state, email) {
      state.userData.email = email;
    },
    SET_PRESCRIPTION(state, prescription) {
      state.latestPrescription = prescription;
    },
    SET_HEALTH_ID(state, payload) {
      if (payload && payload.health_id &&(!state.userData.health_id || state.userData.health_id === '')){
        state.userData.health_id = payload.health_id;
      }
    },
    SET_CASE_ID(state, order) {
      state.case = order
    },
    SET_ENCOUNTER_ID(state, order) {
      state.encounter = order
    },
    SET_DOC_PROFILE(state, doctorProfile) {
      state.doctorProfile = doctorProfile
    },
    SET_SHIPPING_ADDRESS(state, addressData) {
      state.addressData = addressData
    },
    // Dashboard theme
    SET_THEME(state, theme) {
      state.theme = theme
      document.documentElement.setAttribute('data-theme', theme)
    },
    // Referral
    SET_REFERRAL_LINK(state, link) {
      state.referralLink = link
    },
    // Store visibility
    SET_MED_FORM_SUBMISSION(state, submission) {
      state.medFormSubmission = submission
    },
  },

  actions: {
      setChatProfile({ commit }, profileData) {
        try {
            // Validate required fields
            if (!profileData || !profileData.email || !profileData.first_name) {
                throw new Error('Invalid chat profile data');
            }

            // Store in localStorage for persistence
            localStorage.setItem('chatProfile', JSON.stringify(profileData));
            commit('SET_CHAT_PROFILE', profileData);
            
            return Promise.resolve(profileData);
        } catch (error) {
            console.error('Error setting chat profile:', error);
            return Promise.reject(error);
        }
    },
    updateUserPhone({ commit }, phone) {
      commit('SET_NEW_PHONE', phone);
    },
    updateUserEmail({ commit }, email) {
      commit('SET_NEW_EMAIL', email);
    },
    setActiveHomeTab({ commit }, tab) {
        commit('SET_ACTIVE_HOME_TAB', tab);
    },
    addSemaOffer({ commit }, offer) {
      commit('SET_SEMA_OFFER', offer);
    },
    toggeleDocChat({ commit }) {
      commit('SET_DOC_CHAT');
    },
    saveShippingAddress({ commit }, addressData) {
      commit('SET_SHIPPING_ADDRESS', addressData);
    },
    addTirOffer({ commit }, offer) {
      commit('SET_TIR_OFFER', offer);
    },
    updateTransactionItems({ commit }, payload) {
      commit('SET_Transactions_ITEMS', payload);
    },
    updateSubscriptions({ commit }, payload) {
      commit('SET_SUBSCRIPTION_ITEMS', payload);
    },
    updateProgress({ commit }, payload) {
      commit('SET_PROGRESS_ITEMS', payload);
    },
    setPrescriptionPreauths({ commit }, preauths) {
      commit('SET_PRESCRIPTION_PREAUTHS', preauths);
    },
    UpdateUserData({ commit }, userData) {
      commit('updateUserData', userData)
      return Promise.resolve() // Return a promise for proper async handling
    },
    removePrescription({ commit }, orderId) {
      commit('REMOVE_PRESCRIPTION', orderId);
    },
    // Existing actions remain the same
    saveTransaction({ commit }, data) {
      commit('SaveTransactions', data);
    },
    saveShippingDetails({ commit }, data) {
      commit('SaveShippingDetails', data);
    },
    doLogin({ commit }, loginData) {
      commit('updateAccessToken', loginData.token.token);
      commit('updateUserData', loginData.user);

      try {
        if (window.GuestChatWidget && typeof window.GuestChatWidget.identify === 'function') {
          setTimeout(() => {
            window.GuestChatWidget.identify({
              jwt: loginData.token.token,
              userId: loginData.user.id,
              userName: `${loginData.user.first_name} ${loginData.user.last_name}`,
              userEmail: loginData.user.email
            }).catch(err => console.error('Widget identify error:', err));
          }, 100);
        }
      } catch (error) {
        console.log('Error setting guest login on widget:', error);
      }
    },
    
    logout({ commit }, data) {
      return new Promise((resolve, reject) => {
        let token = data.token;
        axios.get("/api/logout", {
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          }
        }).then(async res => {
          commit('updateAccessToken', null);
          console.log(res);

          if (window.GuestChatWidget && typeof window.GuestChatWidget.logout === 'function') {
            try {
              window.GuestChatWidget.logout();
              await new Promise(resolve => setTimeout(resolve, 200));
            } catch (err) {
              console.error('Error logging out from widget:', err);
            }
          }

          localStorage.removeItem('_ymain_key_');
          router.push({ name: 'LoginPage' });
        }).catch(error => {
          reject(error)
        });
      });
    },

    async getIpAddress({ commit }) {
      try {
        const response = await axios.get('https://cors-anywhere.herokuapp.com/http://api.ipify.org/?format=text');
        commit('setIpAddress', response.data);
      } catch (error) {
        console.error('Error fetching IP address:', error);
      }
    },

    // Cart actions
    addToCart({ commit }, item) {
      
      commit('ADD_TO_CART', item);
      commit('SET_CART_VISIBILITY', true);
    },
    
    removeFromCart({ commit }, itemId) {
      commit('REMOVE_FROM_CART', itemId);
    },
    
    updateCartQuantity({ commit }, { itemId, quantity }) {
      commit('UPDATE_CART_QUANTITY', { itemId, quantity });
    },
    
    updateCartItem({ commit }, { itemId, updates }) {
      commit('UPDATE_CART_ITEM', { itemId, updates });
    },

    clearCart({ commit }) {
      commit('CLEAR_CART');
    },
    
    setCartVisibility({ commit }, isVisible) {
      commit('SET_CART_VISIBILITY', isVisible);
    },

    // Store cart actions (dashboard store - separate from legacy)
    storeAddToCart({ commit }, item) {
      commit('STORE_ADD_TO_CART', item);
    },

    storeRemoveFromCart({ commit }, itemId) {
      commit('STORE_REMOVE_FROM_CART', itemId);
    },

    storeUpdateCartQuantity({ commit }, { itemId, quantity }) {
      commit('STORE_UPDATE_CART_QUANTITY', { itemId, quantity });
    },

    storeUpdateCartItem({ commit }, { itemId, updates }) {
      commit('STORE_UPDATE_CART_ITEM', { itemId, updates });
    },

    storeClearCart({ commit }) {
      commit('STORE_CLEAR_CART');
    },

    // New product-related actions
    setCategory({ commit }, category) {
      commit('SET_CURRENT_CATEGORY', category);
    },
    updateHealth({ commit }, payload) {
      commit('SET_HEALTH_ID', payload);
    },
    setLoading({ commit }, status) {
      commit('SET_LOADING', status);
    },
    
    setError({ commit }, error) {
      commit('SET_ERROR', error);
    },
    setPrescription({ commit }, prescription) {
      commit('SET_PRESCRIPTION', prescription);
    },
    setCaseID({ commit }, order) {
      commit('SET_CASE_ID', order);
    },
    setEncounterId({ commit }, order) {
      commit('SET_ENCOUNTER_ID', order);
    },

    setDocProfile({ commit }, docProfile) {
      commit('SET_DOC_PROFILE', docProfile);
    },
    // Dashboard theme actions
    toggleTheme({ commit, state }) {
      const newTheme = state.theme === 'light' ? 'dark' : 'light'
      commit('SET_THEME', newTheme)
      localStorage.setItem('_ymain_theme_', newTheme)

      if (window.GuestChatWidget && typeof window.GuestChatWidget.setTheme === 'function') {
        try {
          window.GuestChatWidget.setTheme(newTheme);
        } catch (err) {
          console.error('Error setting widget theme:', err);
        }
      }
    },
    // Applies the saved theme and nothing else. Everything outside the dashboard
    // is a light-only design, so a page that reads prefers-color-scheme for
    // itself turns checkout and the post-purchase screens dark for customers who
    // never asked for it. Only initDashboardTheme is allowed to read the OS.
    initTheme({ commit }) {
      const savedTheme = localStorage.getItem('_ymain_theme_');
      const theme = savedTheme === 'dark' ? 'dark' : 'light';

      commit('SET_THEME', theme);

      if (window.GuestChatWidget && typeof window.GuestChatWidget.setTheme === 'function') {
        try {
          const currentPath = window.location.pathname;
          const isDashboard = currentPath === '/home' || currentPath.startsWith('/home/');
          if (isDashboard) {
            window.GuestChatWidget.setTheme(theme);
          }
        } catch (err) {
          console.error('Error setting widget theme:', err);
        }
      }
    },
    // The dashboard is the one surface that may adopt the OS setting, and it does
    // it once: the first visit writes the result to _ymain_theme_ so from then on
    // it is a stored choice like any other. Every page outside the dashboard then
    // reads that stored choice through initTheme and never guesses for itself.
    initDashboardTheme({ dispatch }) {
      if (localStorage.getItem('_ymain_theme_')) {
        return dispatch('initTheme');
      }

      const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
      localStorage.setItem('_ymain_theme_', prefersDark ? 'dark' : 'light');

      return dispatch('initTheme');
    },
    syncWidgetTheme({ state }) {
      const currentPath = window.location.pathname;
      const isDashboard = currentPath === '/home' || currentPath.startsWith('/home/');

      if (window.GuestChatWidget && typeof window.GuestChatWidget.setTheme === 'function') {
        try {
          if (isDashboard) {
            window.GuestChatWidget.setTheme(state.theme);
          } else {
            window.GuestChatWidget.setTheme('light');
          }
        } catch (err) {
          console.error('Error syncing widget theme:', err);
        }
      }
    },
    // Referral
    setReferralLink({ commit }, link) {
      commit('SET_REFERRAL_LINK', link)
    },
    // Store visibility
    setMedFormSubmission({ commit }, submission) {
      commit('SET_MED_FORM_SUBMISSION', submission)
    },
    clearMedFormSubmission({ commit }) {
      commit('SET_MED_FORM_SUBMISSION', null)
    },
  },

  getters: {
    offerSema: state => state.offerSema,
    docChatStatus: state => state.docChat,
    offerTir: state => state.offerTir,
    lastCheckedProgress: state => state.lastCheckedProgress,
    progressItems: state => state.progressItems,
    user: state => state.userData,
    isAuthenticated: state => state.accessToken !== null,
    isAdmin: state => state.userData?.is_admin === 1,
    authToken: state => state.accessToken,
    transactionItems: state => state.transactionItems,
    lastCheckedTransactions: state => state.lastCheckedTransactions,
    subscription: state => state.subscription,
    activeHomeTab: state => state.activeHomeTab,
    appointment: state => state.appointments,
    prescription: state => state.priscription,
    tracking: state => state.tracking,
    lab: state => state.lab,
    shipping: state => state.shipping,
    medForm: state => state.medForm,
    ipAddress: state => state.ipAddress,
    getLatestEncounter : state => state.encounter,
    chatProfile: state => state.chatProfile,
    hasChatProfile: state => state.chatProfile!=null,
    getLatestPrescription: state => {
      // If progressItems is empty or not an array, return empty array
      if (!state.progressItems || !Array.isArray(state.progressItems) || state.progressItems.length === 0) {
        return [];
      }

      // Collect all prescriptions across all orders
      const allPrescriptions = state.progressItems
        .filter(order => order.prescription_data && Array.isArray(order.prescription_data))
        .flatMap(order => order.prescription_data);
      
      // If we found no prescriptions, return empty array
      if (allPrescriptions.length === 0) {
        return [];
      }
      
      return allPrescriptions;
    },
    // Cart getters
    getShippingAddress:  state => state.addressData,
    cartItems: state => state.cartItems,
    cartItemCount: state => state.cartItems.reduce((total, item) => total + item.quantity, 0),
    cartTotal: state => {
      return state.cartItems.reduce((total, item) => {
        const price = resolveItemPrice(item);
        const qty = Number(item && item.quantity) || 0;
        return total + (price * qty);
      }, 0);
    },
    cartTotalNoDiscount: state => {
      return state.cartItems.reduce((total, item) => {
        const price = resolveItemRetailPrice(item);
        const qty = Number(item && item.quantity) || 0;
        return total + (price * qty);
      }, 0);
    },

    cartDiscount: state => {
      return state.cartItems.reduce((total, item) => {
        const retailPrice = resolveItemRetailPrice(item);
        const currentPrice = resolveItemPrice(item);
        const qty = Number(item && item.quantity) || 0;
        return total + ((retailPrice - currentPrice) * qty);
      }, 0);
    },
    isCartOpen: state => state.isCartOpen,
    // A saved cart keeps the picture link each item was added with, so an item
    // added before the shop's card picture became the product picture would
    // still draw the old, broken one. Each item is drawn with its product's
    // current picture.
    storeCartItems: state => state.storeCartItems.map(item => {
      const image = shopCardImage(getProductById(item.id)[0]);
      return image ? { ...item, image, thumbnail: image } : item;
    }),
    storeCartItemCount: state => state.storeCartItems.reduce((total, item) => total + item.quantity, 0),
    getRemainingPrescriptions: state => state.priscription,
    // New product-related getters
    currentCategory: state => state.currentCategory,
    loading: state => state.loading,
    error: state => state.error,
    lastCheckedSubscription: state => state.lastCheckedSubscription,
    subscriptionItems: state => state.subscriptionItems,
    getCase: state => {
      // If progressItems is empty or not an array, return null
      if (!state.progressItems || !Array.isArray(state.progressItems) || state.progressItems.length === 0) {
        return null;
      }
      // Filter to orders that have a case with provider === 1
      const ordersWithValidCase = state.progressItems.filter(order => 
        order.external_visit_id && order.provider === 1
      );
      
      if (ordersWithValidCase.length === 0) {
        return null;
      }
      // If there's only one valid case, return it
      // If there are multiple, return the first one (most recent)
      return {id: ordersWithValidCase[0].external_visit_id,  provider: ordersWithValidCase[0].provider};
    },
    docProfile: state => state.doctorProfile,
    // Dashboard theme getter
    isDarkMode: state => state.theme === 'dark',
    // Add this new getter to get all orders with prescriptions
    getOrdersWithPrescriptions: state => {
      if (!state.progressItems || !Array.isArray(state.progressItems)) {
        console.log("returning here")
        return [];
      }
      // An order counts as having a prescription if it has a snapshot (the exact
      // meds sent for that order) or, for older orders with no snapshot, the
      // legacy prescription_data list — mirrors resolvePrescriptions in the
      // dosage components.
      const hasItems = value => Array.isArray(value) && value.length > 0;

      return state.progressItems.filter(order =>
        order && (hasItems(order.prescription_snapshot_data) || hasItems(order.prescription_data))
      );
    },
    // Referral getter
    referralLink: state => state.referralLink,
    // Store visibility getter
    // Store hides only for active sema/tirz subscriptions after 12h + external_visit_id
    isStoreVisible: state => {
      const orders = state.progressItems
      const subs = state.subscriptionItems
      if (!orders || !Array.isArray(orders) || orders.length === 0) return true

      // If there's a pending_approval order, store is always visible
      if (orders.some(o => o.status === 'pending_approval')) return true

      // Check for active sema/tirz subscription
      const hasActiveSemaTirzSub = subs && Array.isArray(subs) && subs.some(s => {
        if (s.status !== 'active') return false
        const name = (s.plan_name || '').toLowerCase()
        return name.includes('semaglutide') || name.includes('tirzepatide')
      })
      // No active sema/tirz subscription → store always visible
      if (!hasActiveSemaTirzSub) return true

      // Active statuses where order is being processed
      const processingStatuses = [
        'waiting_prescription',
        'prescription_received',
        'order_placed',
        'order_shipped',
        'order_delivered'
      ]

      const TWELVE_HOURS = 12 * 60 * 60 * 1000
      const submission = state.medFormSubmission

      // Mode 1: localStorage has tracked submission
      if (submission) {
        const elapsed = Date.now() - submission.timestamp
        if (elapsed < TWELVE_HOURS) return true

        const order = orders.find(o => o.id === submission.orderId)
        if (!order) return true

        return !order.external_visit_id
      }

      // Mode 2: no localStorage — fallback to order data
      const activeOrder = orders.find(o =>
        processingStatuses.includes(o.status) &&
        o.order_type === 'prescription' &&
        o.external_visit_id
      )
      if (!activeOrder) return true

      const elapsed = Date.now() - new Date(activeOrder.updated_at).getTime()
      if (elapsed < TWELVE_HOURS) return true

      return false
    },
    medFormSubmission: state => state.medFormSubmission,
  },

  plugins: [
    createPersistedState({
      key: '_ymain_key_',
      paths: [
        'accessToken', 
        'userData', 
        'transaction', 
        'subscription', 
        'appointments', 
        'lab', 
        'priscription', 
        'shipping', 
        'tracking',
        'ipAddress',
        'medForm',
        'cartItems',
        'isCartOpen',
        'storeCartItems',
        'currentCategory',
        'progressItems', 
        'lastCheckedProgress',
        'subscriptionItems',
        'lastCheckedSubscription',
        'transactionItems',
        'lastCheckedTransactions',
        'offerTir',
        'offerSema',
        'trackingData',
        'chatProfile',
        'latestPrescription',
        'case',
        'doctorProfile',
        'encounter',
        'addressData',
        'referralLink',
        'medFormSubmission'
      ]
    })
  ],
})
